import { Box, Button, styled } from '@mui/material';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { COLORS } from '../colors';

export const LoginContainer = styled(Box)({
  minHeight: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const LoginCard = styled(Box)({
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '48px',
  width: '100%',
  maxWidth: '400px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  textAlign: 'center',
});

export const SSOButton = styled(Button)({
  backgroundColor: '#0078d4',
  color: 'white',
  padding: '12px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#106ebe',
  },
});

export const PaymentContainer = styled(Box)({
  minHeight: '100vh',
  padding: '24px 16px',
  '@media (min-width: 600px)': {
    padding: '24px 32px',
  }
});

export const PaymentCard = styled(Box)({
  maxWidth: '800px',
  margin: '64px auto 0',
  backgroundColor: COLORS.white,
  borderRadius: '8px',
  border: `1px solid ${COLORS.tableHeaderBorder}`,
  boxShadow: `0px 4px 20px ${COLORS.boxShadow}`,
  padding: '16px',
  '@media (min-width: 600px)': {
    padding: '32px',
  }
});

export const PaymentHeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '32px',
  borderBottom: `1px solid ${COLORS.lightGrey}`,
  paddingBottom: '16px'
});

export const PayButton = styled('button')({
  backgroundColor: COLORS.darkBlue,
  color: COLORS.white,
  padding: '12px 16px',
  border: 'none',
  borderRadius: '8px',
  width: '100%',
  marginTop: '24px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 500,
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
  }
});

export const SectionContainer = styled(Box)({
  marginBottom: '32px'
});

export const COMMON_STYLES = {
  layout: {
    fullScreenCenter: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: COLORS.paleBlue,
      p: 3
    },
    flexRowBetween: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    flexRowCenter: {
      display: 'flex',
      gap: 2,
      justifyContent: 'center',
      flexWrap: 'wrap'
    }
  },
  card: {
    primary: {
      width: '100%',
      maxWidth: 600,
      p: { xs: 3, sm: 6 },
      textAlign: 'center',
      bgcolor: COLORS.white,
      borderRadius: 3,
      boxShadow: `0 4px 24px ${COLORS.boxShadow}`
    },
    iconContainer: {
      width: 80,
      height: 80,
      mx: 'auto',
      mb: 4,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  },
  text: {
    heading: {
      mb: 2,
      fontWeight: 700,
      color: COLORS.darkText
    },
    body: {
      mb: 4,
      color: COLORS.grey
    },
    monospace: {
      fontFamily: 'monospace',
      bgcolor: COLORS.paleGrey,
      px: 1.5,
      py: 0.5,
      borderRadius: 1,
      fontSize: '0.875rem'
    }
  },
  payment: {
    detailsContainer: {
      mb: 4,
      p: 3,
      bgcolor: COLORS.paleBlue,
      borderRadius: 2,
    },
    detailsGrid: {
      display: 'grid',
      gap: 2,
    }
  },
  button: {
    primary: {
      borderRadius: '64px',
      fontWeight: 700,
      height: { xs: 40, sm: 64 },
      px: { xs: 2, sm: 3 },
      py: { xs: 1, sm: 2 },
      fontSize: { xs: 14, sm: 18 },
    },
    outlined: {
      borderColor: COLORS.black,
      color: COLORS.black,
      background: COLORS.white,
      '&:hover': { background: COLORS.tableRowEvenBg },
    },
    danger: {
      background: COLORS.statusDeclined,
      color: COLORS.white,
      '&:hover': { background: COLORS.statusDeclined },
    }
  },
  table: {
    container: {
      borderRadius: 4,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    },
    headerCell: {
      fontWeight: 700,
      fontSize: '18px',
      color: COLORS.black,
      height: '90px',
      textAlign: 'center',
    },
    headerCellBackground: {
      background: COLORS.tableHeaderBg,
    },
    cell: {
      fontSize: '18px',
      textAlign: 'center',
      color: COLORS.black,
    }
  }
};

const commonTextFieldSx = {
  background: '#fff',
  borderRadius: '8px',
  '.MuiFilledInput-root': {
    borderRadius: '8px',
    background: '#fff',
    border: '1px solid',
    borderColor: COLORS.borderGray,
    fontSize: '16px',
    fontWeight: 400,
  },
  '.MuiFilledInput-root:hover': {
    background: '#fff',
  },
  '.MuiFilledInput-root.Mui-focused': {
    border: `1.5px solid ${COLORS.filterBtnBorder}`,
    background: '#fff',
  },
  '.MuiFilledInput-root:after': {
    borderBottom: 'none !important',
  },
  '.MuiFilledInput-root:before': {
    borderBottom: 'none !important',
  },
  '.MuiInputBase-input': {
    fontSize: '16px',
    fontWeight: 400,
  },
  '.MuiInputLabel-root': {
    fontSize: '16px',
    fontWeight: 400,
  },
  '.MuiInputLabel-root.Mui-focused': {
    color: COLORS.secondaryGrey,
    fontWeight: 600,
    fontSize: '16px',
  },
  '& .MuiSelect-select': {
    background: 'transparent !important'
  }
};

const commonInputLabelProps = { style: { fontSize: '16px', fontWeight: 400 } };
const commonInputProps = { style: { fontWeight: 400, fontSize: '16px' } };

export const stripeElementsOptions: StripeElementsOptions['appearance'] = {
  theme: "flat",
  variables: {
    colorPrimary: COLORS.darkBlue,
    colorBackground: COLORS.white,
    colorText: COLORS.darkGrey,
    colorDanger: COLORS.errorRed,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI"',
    borderRadius: '4px',
    spacingUnit: '4px',
    fontSizeBase: '14px'
  },
  rules: {
    '.Label': {
      marginBottom: '8px',
      color: COLORS.textGrey
    },
    '.Input': {
      padding: '12px',
      border: `1px solid ${COLORS.lightGrey}`,
      boxShadow: 'none'
    },
    '.Input:focus': {
      border: `1px solid ${COLORS.darkBlue}`,
      boxShadow: `0 0 0 1px ${COLORS.darkBlue}`
    },
    '.Error': {
      color: 'var(--colorDanger)'
    }
  }
};

const requiredAsteriskSx = {
  '& .MuiFormLabel-asterisk': {
    color: COLORS.requiredAsterisk,
  },
};

export {
  commonTextFieldSx,
  commonInputLabelProps,
  commonInputProps,
  requiredAsteriskSx
};